<template>
  <Modal
    :show="show"
    size="md"
    dialogMaxWidth="640px"
    :closeOnBackdrop="false"
    :blurBackdrop="false"
    :backdropOpacity="0.5"
    :zIndex="10060"
    :show-close-button="false"
    @close="$emit('close')"
  >
    <template #header>
      <div class="sms-header__text">
        <div class="sms-header__icon-title">
          <div class="sms-header__icon-container">
            <Icon icon="mdi:message-text-outline" class="sms-header__icon" />
          </div>
          <div>
            <h2 class="sms-header__title">Start New SMS Chat</h2>
            <p class="sms-header__subtitle">
              Select which phone numbers to use for this conversation
            </p>
          </div>
        </div>
      </div>
      <button type="button" class="sms-header__close" @click="$emit('close')" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>
    <template #body>
      <form id="sms-chat-form" @submit.prevent="handleSubmit" class="sms-form">
        <!-- Send From (Organization Phone Number) -->
        <div class="sms-field">
          <Select
            v-model="formData.phoneNumberId"
            label="Send From"
            placeholder="Select your phone number"
            :options="orgPhoneOptions"
            :error="errors.phoneNumberId"
            required
          />
          <p v-if="orgPhoneNumbers.length === 0" class="sms-no-phones">
            <Icon icon="mdi:alert-circle-outline" />
            No phone numbers available. Please purchase a Twilio number first.
          </p>
        </div>

        <!-- Send To (Contact Phone Number) -->
        <div class="sms-field">
          <Select
            v-model="formData.contactPhoneId"
            label="Send To"
            placeholder="Select contact's phone number"
            :options="contactPhoneOptions"
            :error="errors.contactPhoneId"
            required
            @update:model-value="handleContactPhoneSelect"
          />
          <p v-if="contactPhoneNumbers.length === 0" class="sms-no-phones">
            <Icon icon="mdi:alert-circle-outline" />
            This contact has no phone numbers. Please add a phone number first.
          </p>
        </div>

        <!-- Selected Phone Number Display -->
        <div v-if="formData.contactPhoneNumber" class="sms-selected-phone">
          <Icon icon="mdi:phone-outline" />
          <span>{{ formData.contactPhoneNumber }}</span>
        </div>

        <!-- Message -->
        <div class="sms-field">
          <label class="sms-label">
            Message
            <span class="sms-optional">(Optional)</span>
          </label>
          <textarea
            v-model="formData.message"
            class="sms-textarea"
            placeholder="Enter your message here..."
            rows="4"
          ></textarea>

          <div class="sms-ai-row">
            <button
              ref="aiComposeButtonRef"
              type="button"
              class="sms-ai-btn sms-ai-btn--compose"
              :disabled="isGeneratingAiCompose"
              :title="isGeneratingAiCompose ? 'Generating...' : 'AI Compose'"
              @click="toggleAiComposePanel"
            >
              <Icon icon="mdi:sparkles" class="sms-ai-btn-icon" />
              <span>{{ isGeneratingAiCompose ? "Generating..." : "AI Compose" }}</span>
            </button>
            <button
              ref="aiRefineButtonRef"
              type="button"
              class="sms-ai-btn"
              :disabled="isGeneratingAiCustom"
              :title="isGeneratingAiCustom ? 'Generating...' : 'AI Refine'"
              @click="toggleAiRefinePanel"
            >
              <Icon icon="mdi:sparkles" class="sms-ai-btn-icon" />
              <span>AI Refine</span>
            </button>
          </div>

          <AiRefinePopover
            :show="showAiPanel"
            :trigger-el="aiRefineButtonRef"
            :disabled="isGeneratingAiCustom"
            @generate="handleAiCustomizationGenerate"
            @close="showAiPanel = false"
          />

          <AiRefinePopover
            :show="showAiComposePanel"
            :trigger-el="aiComposeButtonRef"
            :disabled="isGeneratingAiCompose"
            title="AI Compose"
            generate-button-label="Generate Message"
            :show-attachments="false"
            @generate="handleAiComposeGenerate"
            @close="showAiComposePanel = false"
          />
        </div>
      </form>
    </template>
    <template #footer>
      <Button variant="ghost" size="md" @click="$emit('close')" :disabled="isLoading">
        Cancel
      </Button>
      <Button
        type="submit"
        form="sms-chat-form"
        variant="primary"
        size="md"
        icon="mdi:message-text-outline"
        icon-position="left"
        :loading="isLoading"
        :disabled="!canSubmit"
      >
        Start Conversation
      </Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { Button, Modal, Select } from "@/components/ui";
import type { SelectOption } from "@/components/ui/types";
import AiRefinePopover from "@/components/socials/AiRefinePopover.vue";
import { Icon } from "@iconify/vue";
import { computed, ref, watch } from "vue";
import AIGenerationService from "@/services/ai/ai-generation";
import { useToast } from "@/composables/ui/useToast";
import { useResolvedAiPrompts } from "@/composables/useAiPrompts";

interface OrgPhoneNumber {
  id: string;
  number: string;
  region?: string;
  city?: string;
}

interface ContactPhone {
  id: string;
  label: string;
  number: string;
}

interface Props {
  show: boolean;
  orgPhoneNumbers: OrgPhoneNumber[];
  contactPhoneNumbers: ContactPhone[];
  contactId: string;
  isLoading?: boolean;
  initialMessage?: string;
  icpId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  initialMessage: "",
  orgPhoneNumbers: () => [],
  contactPhoneNumbers: () => [],
});

interface CreateSMSChatRequest {
  contactId: string;
  phoneNumberId: string;
  contactPhoneNumber: string;
  contactPhoneId?: string;
  message?: string;
}

const emit = defineEmits<{
  close: [];
  "create-chat": [request: CreateSMSChatRequest];
}>();

const $toast = useToast();

// Form data
const formData = ref({
  phoneNumberId: "",
  contactPhoneId: "",
  contactPhoneNumber: "",
  message: "",
});

const errors = ref<{
  phoneNumberId?: string;
  contactPhoneId?: string;
}>({});

const showAiPanel = ref(false);
const showAiComposePanel = ref(false);
const isGeneratingAiCustom = ref(false);
const isGeneratingAiCompose = ref(false);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const aiComposeButtonRef = ref<HTMLElement | null>(null);
const resolvedAiPromptsQuery = useResolvedAiPrompts();

const toggleAiRefinePanel = () => {
  showAiComposePanel.value = false;
  showAiPanel.value = !showAiPanel.value;
};

const toggleAiComposePanel = () => {
  showAiPanel.value = false;
  showAiComposePanel.value = !showAiComposePanel.value;
};

const handleAiComposeGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCompose.value) return;
  isGeneratingAiCompose.value = true;
  try {
    const response = await AIGenerationService.generateCompose({
      contactId: props.contactId,
      icpId: props.icpId,
      channel: "sms",
      customInstructions: payload.customInstructions,
      aiPromptCompose: resolvedAiPromptsQuery.composeAiPrompt.value,
    });

    if (response.body) {
      formData.value.message = response.body;
      showAiComposePanel.value = false;
      $toast?.success("Message composed successfully");
    }
  } catch (error: any) {
    console.error("Failed to compose message:", error);
    $toast?.error(error.message || "Failed to compose message");
  } finally {
    isGeneratingAiCompose.value = false;
  }
};

const handleAiCustomizationGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCustom.value) return;
  isGeneratingAiCustom.value = true;
  try {
    const response = await AIGenerationService.generateRefine({
      contactId: props.contactId,
      icpId: props.icpId,
      channel: "sms",
      currentDraft: formData.value.message || "",
      customInstructions: payload.customInstructions,
    });

    if (response.body) {
      formData.value.message = response.body;
      showAiPanel.value = false;
      $toast?.success("Message refined successfully");
    }
  } catch (error: any) {
    console.error("Failed to refine message:", error);
    $toast?.error(error.message || "Failed to refine message");
  } finally {
    isGeneratingAiCustom.value = false;
  }
};

// Organization phone number options
const orgPhoneOptions = computed<SelectOption[]>(() => {
  return props.orgPhoneNumbers.map((phone) => ({
    value: phone.id,
    label: phone.city ? `${phone.number} (${phone.city})` : phone.number,
  }));
});

const contactPhoneOptions = computed<SelectOption[]>(() => {
  return props.contactPhoneNumbers.map((phone) => ({
    value: phone.id,
    label: `${phone.label}: ${phone.number}`,
  }));
});

// Check if form can be submitted
const canSubmit = computed(() => {
  return (
    formData.value.phoneNumberId &&
    formData.value.contactPhoneNumber &&
    props.orgPhoneNumbers.length > 0 &&
    props.contactPhoneNumbers.length > 0 &&
    !errors.value.phoneNumberId &&
    !errors.value.contactPhoneId
  );
});

const handleContactPhoneSelect = (value: string | number) => {
  const phoneId = String(value);
  const phone = props.contactPhoneNumbers.find((p) => p.id === phoneId);
  if (phone) {
    formData.value.contactPhoneNumber = phone.number;
    formData.value.contactPhoneId = phone.id;
  }
};

const handleSubmit = () => {
  errors.value = {};

  if (!formData.value.phoneNumberId) {
    errors.value.phoneNumberId = "Please select a phone number to send from";
    return;
  }

  if (!formData.value.contactPhoneNumber) {
    errors.value.contactPhoneId = "Please select a phone number to send to";
    return;
  }

  const request: CreateSMSChatRequest = {
    contactId: props.contactId,
    phoneNumberId: formData.value.phoneNumberId,
    contactPhoneNumber: formData.value.contactPhoneNumber,
    contactPhoneId: formData.value.contactPhoneId || undefined,
    message: formData.value.message?.trim() || undefined,
  };

  emit("create-chat", request);
};

// Auto-select if only one option
watch(
  () => props.show,
  (isShowing) => {
    if (isShowing) {
      // Auto-select org phone number if only one
      if (props.orgPhoneNumbers.length === 1) {
        formData.value.phoneNumberId = props.orgPhoneNumbers[0].id;
      }

      if (props.contactPhoneNumbers.length === 1) {
        const phone = props.contactPhoneNumbers[0];
        formData.value.contactPhoneId = phone.id;
        formData.value.contactPhoneNumber = phone.number;
      }

      if (props.initialMessage) {
        formData.value.message = props.initialMessage;
      }
    } else {
      // Reset form when modal closes
      formData.value = {
        phoneNumberId: "",
        contactPhoneId: "",
        contactPhoneNumber: "",
        message: "",
      };
      errors.value = {};
      showAiPanel.value = false;
      showAiComposePanel.value = false;
    }
  },
  { immediate: true }
);

// Watch for changes in phone numbers to auto-select
watch(
  () => [props.orgPhoneNumbers, props.contactPhoneNumbers],
  () => {
    if (props.show) {
      if (props.orgPhoneNumbers.length === 1 && !formData.value.phoneNumberId) {
        formData.value.phoneNumberId = props.orgPhoneNumbers[0].id;
      }
      if (props.contactPhoneNumbers.length === 1 && !formData.value.contactPhoneId) {
        const phone = props.contactPhoneNumbers[0];
        formData.value.contactPhoneId = phone.id;
        formData.value.contactPhoneNumber = phone.number;
      }
    }
  },
  { deep: true }
);
</script>

<style scoped>
.sms-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sms-header__icon-title {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.sms-header__icon-container {
  width: 40px;
  height: 40px;
  background: #3e3efe;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sms-header__icon {
  font-size: 24px;
  color: white;
}

.sms-header__title {
  margin: 0 0 4px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
  letter-spacing: -0.01em;
}

.sms-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: #646464;
}

.sms-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #646464;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.sms-header__close:hover {
  background: #e8e8f0;
  color: #1a1a2e;
}

.sms-form {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  max-height: calc(75vh - 130px);
}

.sms-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sms-no-phones {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #dc2626;
  margin: 0;
}

.sms-no-phones .iconify {
  font-size: 1rem;
}

.sms-selected-phone {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f3f4ff;
  border: 1px solid #e0e7ff;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #3e3efe;
  font-weight: 500;
}

.sms-selected-phone .iconify {
  font-size: 1.125rem;
}

.sms-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.sms-optional {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6b7280;
}

.sms-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;
  min-height: 100px;
}

.sms-textarea:focus {
  border-color: #3e3efe;
}

.sms-textarea::placeholder {
  color: #9ca3af;
}

.sms-ai-row {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.sms-ai-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.7rem;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
  background: rgba(107, 114, 128, 0.12);
  color: #4b5563;
}

.sms-ai-btn:hover:not(:disabled) {
  background: rgba(107, 114, 128, 0.2);
  color: #374151;
}

.sms-ai-btn--compose {
  background: rgba(8, 145, 178, 0.1);
  color: #0891b2;
}

.sms-ai-btn--compose .sms-ai-btn-icon {
  color: #0891b2;
}

.sms-ai-btn--compose:hover:not(:disabled) {
  background: rgba(8, 145, 178, 0.2);
  color: #0e7490;
}

.sms-ai-btn--compose:hover:not(:disabled) .sms-ai-btn-icon {
  color: #0e7490;
}

.sms-ai-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sms-ai-btn-icon {
  font-size: 0.75rem;
  color: #4b5563;
}

.sms-ai-btn:hover:not(:disabled) .sms-ai-btn-icon {
  color: #374151;
}
</style>
